import styles from './styles.module.scss';

import { School, SchoolOptions, Location, User } from '../../../../../../types';
import { Input } from '../../../../../../components/Input';
import { FormControl } from '../../../../../../components/FormControl';
import ProfileImage from '../../../../../../assets/images/icons/profile/white-mode-school-profile.png';
import { Select, SelectOption } from '../../../../../../components/Select';
import { useContext, useEffect, useRef, useState } from 'react';
import { Button } from '../../../../../../components/Button';
import { capitalize, hasPermission, maxImageUpload } from '../../../../../../globalFunctions';
import { MessageContext } from '../../../../../../providers/MessageProvider';
import axios from 'axios';

type LogicManagerSchoolDetailProps = {
    school: School | undefined;
    setSchool: React.Dispatch<React.SetStateAction<School | undefined>>;
    options: SchoolOptions;
    onSave: React.MouseEventHandler<HTMLButtonElement>;
    onAdd: React.MouseEventHandler<HTMLButtonElement>;
    onRestore: React.MouseEventHandler<HTMLButtonElement>;
    onDelete: React.MouseEventHandler<HTMLButtonElement>;
    userAuth: User;
};

export const LogicManagerSchoolDetail = ({
    school,
    setSchool,
    options,
    onSave,
    onAdd,
    onRestore,
    onDelete,
    userAuth
}: LogicManagerSchoolDetailProps) => {
    const { setOpenMessage, setMessage } = useContext(MessageContext);
    const isAdmin = hasPermission('logicmanager.admin', userAuth);
    const imageInputRef = useRef<HTMLInputElement>(null);

    // IBGE API
    const [countries, setCountries] = useState<SelectOption[]>([]);
    const [states, setStates] = useState<SelectOption[]>([]);
    const [cities, setCities] = useState<SelectOption[]>([]);

    const handleImage = () => {
        if (imageInputRef.current) {
            imageInputRef.current.click();
        }
    };

    const onImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            if (event.target.files && event.target.files.length > 0 && school) {
                const selectedImage = event.target.files[0];

                if (selectedImage.size > maxImageUpload()) {
                    if (imageInputRef.current) {
                        imageInputRef.current.value = '';
                    }

                    throw new Error('Imagem excede o limite de 10MB');
                } else {
                    const reader = new FileReader();

                    reader.onloadend = () => {
                        school.image = {
                            id: 0,
                            url: reader.result as string,
                            file: selectedImage,
                            removeId: school.image && school.image.id ? school.image.id : 0
                        };

                        setSchool({ ...school });
                    };
                    reader.readAsDataURL(selectedImage);
                }
            }
        } catch (error: any) {
            setMessage({
                type: 'error',
                content: error.message ?? 'Erro ao fazer upload da imagem'
            });
            setOpenMessage(true);
        }
    };

    const onFormControlChange = (event: any, prop: string) => {
        if (school) {
            switch (prop) {
                case 'chain':
                case 'type':
                case 'scope':
                    if (event.target.value !== '') {
                        options[`school${capitalize(prop)}s`].forEach((option: SelectOption) => {
                            if (option.value == event.target.value) {
                                school[prop] = {
                                    id: option.value,
                                    internal_type: option.key,
                                    name: option.text
                                };

                                if (prop === 'type' && option.key === 'PRIVATE') {
                                    school.scope = null;
                                }
                            }
                        });
                    } else {
                        school[prop] = {
                            id: 0,
                            internal_type: '',
                            name: ''
                        };
                    }
                    break;

                case 'has_moodle_server':
                    const value = parseInt(event.target.value);
                    school[prop] = value;

                    if (value === 0) {
                        school.moodle_server = '';
                    }
                    break;

                case 'country':
                    school[prop] = event.target.value;

                    if (event.target.value !== 'Brasil') {
                        school.state = '';
                        school.city = '';
                    }
                    break;

                default:
                    school[prop] = event.target.value;
            }

            setSchool({ ...school });
        }
    };

    const loadCountries = async () => {
        try {
            axios.defaults.withCredentials = false;

            const response = await axios.get(
                'https://servicodados.ibge.gov.br/api/v1/localidades/paises?orderBy=nome'
            );

            const options: SelectOption[] = [];

            response.data.forEach((country: Location) => {
                options.push({
                    key: country.id['M49'],
                    value: country.nome,
                    text: country.nome,
                    description: country.id['ISO-ALPHA-3']
                });
            });

            setCountries(options);

            // Load states if school country has already been selected
            if (school?.country === 'Brasil') {
                await loadStates();
            }
        } catch (error: any) {
            setMessage({
                type: 'error',
                content: 'Erro ao buscar países'
            });
            setOpenMessage(true);
        } finally {
            axios.defaults.withCredentials = true;
        }
    };

    const loadStates = async () => {
        try {
            axios.defaults.withCredentials = false;

            const response = await axios.get(
                `https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome`
            );

            const options: SelectOption[] = [];

            response.data.forEach((state: Location) => {
                options.push({
                    key: state.id,
                    value: state.nome,
                    text: state.nome,
                    description: state.sigla
                });
            });

            setStates(options);

            // Load cities if school state has already been selected
            if (school?.state !== '') {
                response.data.forEach(async (state: Location) => {
                    if (school?.state === state.nome) {
                        await loadCities(typeof state.id === 'number' ? state.id : 0);
                    }
                });
            }
        } catch (error: any) {
            setMessage({
                type: 'error',
                content: 'Erro ao buscar estados'
            });
            setOpenMessage(true);
        } finally {
            axios.defaults.withCredentials = true;
        }
    };

    const loadCities = async (UF: number) => {
        try {
            axios.defaults.withCredentials = false;

            const response = await axios.get(
                `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${UF}/municipios?orderBy=nome`
            );

            const options: SelectOption[] = [];

            response.data.forEach((city: Location) => {
                options.push({
                    key: city.id,
                    value: city.nome,
                    text: city.nome
                });
            });

            setCities(options);
        } catch (error: any) {
            setMessage({
                type: 'error',
                content: 'Erro ao buscar cidades'
            });
            setOpenMessage(true);
        } finally {
            axios.defaults.withCredentials = true;
        }
    };

    useEffect(() => {
        if (school) {
            setStates([]);
            setCities([]);
            loadCountries();
        }
    }, [school?.id]);

    useEffect(() => {
        if (school?.country === 'Brasil') {
            loadStates();
        } else {
            setStates([]);
            setCities([]);
        }
    }, [school?.country]);

    useEffect(() => {
        if (school?.country === 'Brasil' && school?.state !== '') {
            states.forEach(state => {
                if (state.text === school.state) {
                    loadCities(state.key);
                }
            });
        }
    }, [school?.state]);

    return (
        <div className={styles.wrapper}>
            {school ? (
                <>
                    {isAdmin && (
                        
                        <div className={styles.btnBox}>
                            <Button type='success' title='Adicionar' onClick={onAdd}>
                                <div className='fa fa-plus' aria-hidden='true' />
                            </Button>

                            <div className={styles.btnBoxGroup}>
                                {school.is_active ? (
                                    <>
                                        {school.id > 0 && (
                                            <Button
                                                type='error'
                                                title='Desativar'
                                                onClick={onDelete}
                                            >
                                                <div className='fa fa-ban' aria-hidden='true' />
                                            </Button>
                                        )}

                                        <Button type='success' title='Salvar' onClick={onSave}>
                                            <div className='fa fa-check' aria-hidden='true' />
                                        </Button>
                                    </>
                                ) : (
                                    <Button type='warning' title='Ativar' onClick={onRestore}>
                                        <div className='fa fa-undo' aria-hidden='true' />
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                    <div className={styles.container}>
                        <div className={styles.profile}>
                            <div
                                className={`${styles.profileImage} ${
                                    !school.image && styles.fakeImage
                                }`}
                                onClick={handleImage}
                            >
                                <img
                                    src={school.image ? school.image.url : ProfileImage}
                                    alt={`Imagem da escola ${school.name}`}
                                />
                                <div className={styles.hoverImage}>
                                    <div className='fa fa-plus' aria-hidden='true' />
                                    <input
                                        className={styles.imageInput}
                                        type='file'
                                        accept='image/png, image/jpeg, image/jpg'
                                        id='image'
                                        name='image'
                                        onChange={onImageChange}
                                        ref={imageInputRef}
                                        disabled={!school.is_active || !isAdmin}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={styles.row}>
                            <FormControl
                                key='formControlName'
                                flexDirection='column'
                                wrapperClass={styles.five}
                                label='Nome'
                                inputId='name'
                            >
                                <Input
                                    key='inputName'
                                    inputWrapperClass={styles.field}
                                    id='name'
                                    type='text'
                                    value={school.name}
                                    onChange={(e: any) => onFormControlChange(e, 'name')}
                                    disabled={!school.is_active || !isAdmin}
                                />
                            </FormControl>

                            <FormControl
                                key='formControlSlug'
                                flexDirection='column'
                                wrapperClass={styles.one}
                                label='Sigla'
                                inputId='slug'
                            >
                                <Input
                                    key='inputSlug'
                                    inputWrapperClass={styles.field}
                                    id='slug'
                                    type='text'
                                    value={school.slug}
                                    onChange={(e: any) => onFormControlChange(e, 'slug')}
                                    disabled={!school.is_active || !isAdmin}
                                />
                            </FormControl>
                        </div>

                        <div className={styles.row}>
                            {options.schoolChains.length > 0 && (
                                <FormControl
                                    key='formControlSchoolChain'
                                    flexDirection='column'
                                    wrapperClass={styles.five}
                                    label='Rede da Escola'
                                    inputId='chain'
                                >
                                    <Select
                                        key='selectSchoolChain'
                                        selectWrapperClass={styles.field}
                                        id='schoolChain'
                                        options={[
                                            { key: 'empty', value: '', text: 'Nenhuma' }
                                        ].concat(options.schoolChains)}
                                        value={school.chain ? school.chain.id : ''}
                                        onChange={(e: any) => onFormControlChange(e, 'chain')}
                                        disabled={!school.is_active || !isAdmin}
                                    />
                                </FormControl>
                            )}

                            <FormControl
                                key='formControlSchoolType'
                                flexDirection='column'
                                wrapperClass={styles.two}
                                label='Tipo da Escola'
                                inputId='schoolType'
                            >
                                <Select
                                    key='selectSchoolType'
                                    selectWrapperClass={styles.field}
                                    id='schoolType'
                                    options={options.schoolTypes}
                                    showEmptyOption={true}
                                    value={school.type.id !== 0 ? school.type.id : ''}
                                    onChange={(e: any) => onFormControlChange(e, 'type')}
                                    disabled={!school.is_active || !isAdmin}
                                />
                            </FormControl>

                            {school.type.internal_type === 'PUBLIC' && (
                                <FormControl
                                    key='formControlSchoolScope'
                                    flexDirection='column'
                                    wrapperClass={styles.two}
                                    label='Escopo da Escola'
                                    inputId='schoolScope'
                                >
                                    <Select
                                        key='selectSchoolScope'
                                        selectWrapperClass={styles.field}
                                        id='schoolScope'
                                        options={options.schoolScopes}
                                        showEmptyOption={true}
                                        value={school.scope ? school.scope.id : ''}
                                        onChange={(e: any) => onFormControlChange(e, 'scope')}
                                        disabled={!school.is_active || !isAdmin}
                                    />
                                </FormControl>
                            )}
                        </div>

                        <div className={styles.row}>
                            {countries.length > 0 && (
                                <FormControl
                                    key='formControlCountry'
                                    flexDirection='column'
                                    wrapperClass={styles.one}
                                    label='País'
                                    inputId='country'
                                >
                                    <Select
                                        key='selectCountry'
                                        selectWrapperClass={styles.field}
                                        id='country'
                                        options={countries}
                                        value={school.country}
                                        showEmptyOption={true}
                                        onChange={(e: any) => onFormControlChange(e, 'country')}
                                        disabled={!school.is_active || !isAdmin}
                                    />
                                </FormControl>
                            )}

                            {states.length > 0 && school.country === 'Brasil' ? (
                                <FormControl
                                    key='formControlState'
                                    flexDirection='column'
                                    wrapperClass={styles.one}
                                    label='Estado'
                                    inputId='state'
                                >
                                    <Select
                                        key='selectState'
                                        selectWrapperClass={styles.field}
                                        id='state'
                                        options={states}
                                        showEmptyOption={true}
                                        value={school.state}
                                        onChange={(e: any) => onFormControlChange(e, 'state')}
                                        disabled={!school.is_active || !isAdmin}
                                    />
                                </FormControl>
                            ) : (
                                <FormControl
                                    key='formControlState'
                                    flexDirection='column'
                                    wrapperClass={styles.one}
                                    label='Estado'
                                    inputId='state'
                                >
                                    <Input
                                        key='inputState'
                                        inputWrapperClass={styles.field}
                                        id='state'
                                        type='text'
                                        value={school.state}
                                        onChange={(e: any) => onFormControlChange(e, 'state')}
                                        disabled={!school.is_active || !isAdmin}
                                    />
                                </FormControl>
                            )}

                            {cities.length > 0 && school.country === 'Brasil' ? (
                                <FormControl
                                    key='formControlCity'
                                    flexDirection='column'
                                    wrapperClass={styles.one}
                                    label='Cidade'
                                    inputId='city'
                                >
                                    <Select
                                        key='selectCity'
                                        selectWrapperClass={styles.field}
                                        id='city'
                                        options={cities}
                                        showEmptyOption={true}
                                        value={school.city}
                                        onChange={(e: any) => onFormControlChange(e, 'city')}
                                        disabled={!school.is_active || !isAdmin}
                                    />
                                </FormControl>
                            ) : (
                                school.country !== 'Brasil' && (
                                    <FormControl
                                        key='formControlCity'
                                        flexDirection='column'
                                        wrapperClass={styles.one}
                                        label='Cidade'
                                        inputId='city'
                                    >
                                        <Input
                                            key='inputCity'
                                            inputWrapperClass={styles.field}
                                            id='city'
                                            type='text'
                                            value={school.city}
                                            onChange={(e: any) => onFormControlChange(e, 'city')}
                                            disabled={!school.is_active || !isAdmin}
                                        />
                                    </FormControl>
                                )
                            )}
                        </div>

                        <div className={styles.row}>
                            <FormControl
                                key='formControlHasMoodleServer'
                                flexDirection='column'
                                wrapperClass={styles.one}
                                label='Possui Moodle?'
                                inputId='hasMoodleServer'
                            >
                                <Select
                                    key='selectHasMoodleServer'
                                    selectWrapperClass={styles.field}
                                    id='hasMoodleServer'
                                    type='Yes/No'
                                    value={school.has_moodle_server}
                                    onChange={(e: any) =>
                                        onFormControlChange(e, 'has_moodle_server')
                                    }
                                    disabled={!school.is_active || !isAdmin}
                                />
                            </FormControl>

                            {school.has_moodle_server === 1 && (
                                <FormControl
                                    key='formControlMoodleServer'
                                    flexDirection='column'
                                    wrapperClass={styles.five}
                                    label='URL do servidor do Moodle'
                                    inputId='moodleServer'
                                >
                                    <Input
                                        key='inputMoodleServer'
                                        inputWrapperClass={styles.field}
                                        id='moodleServer'
                                        type='text'
                                        value={school.moodle_server}
                                        onChange={(e: any) =>
                                            onFormControlChange(e, 'moodle_server')
                                        }
                                        disabled={!school.is_active || !isAdmin}
                                    />
                                </FormControl>
                            )}
                        </div>
                    </div>
                </>
            ) : (
                <>
                    {isAdmin && (
                        <div className={styles.btnBox}>
                            <Button type='success' title='Adicionar' onClick={onAdd}>
                                <div className='fa fa-plus' aria-hidden='true' />
                            </Button>
                        </div>
                    )}

                    <div className={styles.noSelected}>Selecione uma escola</div>
                </>
            )}
        </div>
    );
};
